import React, {
  PropsWithChildren,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { T_UserSubscriptionDetails } from 'services/subscription/interface';
import { AuthState, useFirebaseAuth } from './useFirebaseAuth';
import type { UserSettingDetails } from 'services/user/api';

const UserSubscriberLoader = React.lazy(
  () => import('components/UserSubscriberLoader')
);

interface UserSettingDoc {
  is_subscriber?: boolean;
}

interface Context {
  isLoading: boolean;
  isSubscriber: boolean;
  setIsSubscriber: (value: boolean) => void;
  isUserWebSubscriptionActive: boolean;
  userSubscriptionDetails?: T_UserSubscriptionDetails | null;
  validateSubscriptionDetails: () => Promise<
    UserSettingDetails | undefined | null
  >;
}

export const UserSubscriberContext = React.createContext<Context | undefined>(
  undefined
);

export const UserSubscriberProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  const { user, authState, isLoading: isAuthLoading } = useFirebaseAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [userSubscriptionDetails, setUserSubscriptionDetails] = useState<
    T_UserSubscriptionDetails | null | undefined
  >(undefined);
  const [isSubscriptionDetailsLoading, setIsSubscriptionDetailsLoading] =
    useState(true);
  const isAuthorized = authState === AuthState.AUTHORIZED;

  // eslint-disable-next-line consistent-return
  const validateSubscriptionDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      const { getUserPrivateSettings } = await import('services/user/api');
      const userPrivateSettings = await getUserPrivateSettings();

      setIsSubscriber(!!userPrivateSettings?.is_subscriber);

      return userPrivateSettings;
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    if (authState === AuthState.AUTHORIZED && user) {
      setIsLoading(true);
      const fetch = async () => {
        const [{ createDocRef, getDocument }] = await Promise.all([
          import('lib/firebase/utils')
        ]);
        const docRef = await createDocRef(`users/${user.uid}/private/settings`);
        const doc = await getDocument(docRef);

        if (!mounted) {
          return;
        }

        if (doc.exists()) {
          const data: UserSettingDoc | undefined = doc.data();
          setIsSubscriber(data?.is_subscriber ? data.is_subscriber : false);
        }
      };

      fetch()
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          if (mounted) {
            setIsLoading(false);
          }
        });
    } else if (authState === AuthState.UNAUTHORIZED) {
      setIsLoading(false);
      setIsSubscriber(false);
    }

    return () => {
      mounted = false;
    };
  }, [authState, user]);

  const { is_subscriber_web } = userSubscriptionDetails ?? {};
  const isUserWebSubscriptionActive = !!is_subscriber_web;

  return (
    <UserSubscriberContext.Provider
      value={{
        isLoading: isLoading || isSubscriptionDetailsLoading || isAuthLoading,
        isSubscriber,
        isUserWebSubscriptionActive,
        userSubscriptionDetails,
        setIsSubscriber,
        validateSubscriptionDetails
      }}
    >
      {children}
      <Suspense fallback={<></>}>
        <UserSubscriberLoader
          isAuthorized={isAuthorized}
          user={user}
          setSubscriptionDetails={setUserSubscriptionDetails}
          setIsSubscriptionDetailsLoading={setIsSubscriptionDetailsLoading}
        />
      </Suspense>
    </UserSubscriberContext.Provider>
  );
};

export function useUserSubscriber() {
  const context = useContext(UserSubscriberContext);
  if (!context) {
    throw new Error(
      'useUserSubscriber needs to be used within UserSubscriberProvider'
    );
  }

  return context;
}
